import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { CONSENT_KEY, readConsent, saveConsent, updateConsent } from "./consent";

export default function ConsentBanner() {
  const { t } = useTranslation("common");

  const [open, setOpen] = useState(false);
  const [customize, setCustomize] = useState(false);
  const [opts, setOpts] = useState({ analytics: false, ads: false });

  // On mount: show banner if no saved choice; otherwise re-apply saved consent
  useEffect(() => {
    const saved = readConsent();
    if (!saved) {
      setOpen(true);
    } else {
      updateConsent(saved);
    }
  }, []);

  if (!open) return null;

  const acceptAll = () => {
    saveConsent("accept_all");
    updateConsent("accept_all");
    setOpen(false);
  };

  const rejectAll = () => {
    saveConsent("reject_all");
    updateConsent("reject_all");
    setOpen(false);
  };

  const saveCustom = () => {
    saveConsent(opts);
    updateConsent(opts);
    setOpen(false);
  };

  // Reset button: clear stored choice AND immediately revert consent to denied
  const resetConsent = () => {
    try {
      localStorage.removeItem(CONSENT_KEY);
    } catch {}
    setOpts({ analytics: false, ads: false });
    setCustomize(false);
    setOpen(true);               // keep banner open so user can choose again
    updateConsent("reject_all"); // flip session back to denied right now
  };

  return (
    <div className="fixed inset-x-0 bottom-0 z-[9999]">
      <div className="mx-auto mb-3 max-w-4xl rounded-2xl border border-black/10 bg-white/90 backdrop-blur px-4 py-3 shadow-lg">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="text-sm text-gray-800">
            {t("consent.message.pre")}{" "}
            <strong>{t("consent.message.analytics")}</strong>{" "}
            {t("consent.message.and")}{" "}
            <strong>{t("consent.message.ads")}</strong>.
          </div>

          {!customize ? (
            <div className="flex gap-2 justify-end">
              <button
                className="rounded-lg px-3 py-2 border hover:bg-gray-50"
                onClick={() => setCustomize(true)}
                aria-label={t("consent.actions.customize")}
              >
                {t("consent.actions.customize")}
              </button>
              <button
                className="rounded-lg px-3 py-2 border hover:bg-gray-50"
                onClick={rejectAll}
                aria-label={t("consent.actions.reject")}
              >
                {t("consent.actions.reject")}
              </button>
              <button
                className="rounded-lg px-3 py-2 bg-redglass-primary text-white hover:brightness-110"
                onClick={acceptAll}
                aria-label={t("consent.actions.accept")}
              >
                {t("consent.actions.accept")}
              </button>
            </div>
          ) : (
            <div className="w-full">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm mb-3">
                <label className="flex items-center gap-2 border rounded-xl p-2">
                  <input type="checkbox" checked disabled readOnly />
                  <span>{t("consent.categories.functional")}</span>
                </label>
                <label className="flex items-center gap-2 border rounded-xl p-2">
                  <input
                    type="checkbox"
                    checked={opts.analytics}
                    onChange={(e) =>
                      setOpts((o) => ({ ...o, analytics: e.target.checked }))
                    }
                  />
                  <span>{t("consent.categories.analytics")}</span>
                </label>
                <label className="flex items-center gap-2 border rounded-xl p-2">
                  <input
                    type="checkbox"
                    checked={opts.ads}
                    onChange={(e) =>
                      setOpts((o) => ({ ...o, ads: e.target.checked }))
                    }
                  />
                  <span>{t("consent.categories.ads")}</span>
                </label>
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  className="rounded-lg px-3 py-2 border hover:bg-gray-50"
                  onClick={() => setCustomize(false)}
                  aria-label={t("consent.actions.back")}
                >
                  {t("consent.actions.back")}
                </button>
                <button
                  className="rounded-lg px-3 py-2 bg-redglass-primary text-white hover:brightness-110"
                  onClick={saveCustom}
                  aria-label={t("consent.actions.save")}
                >
                  {t("consent.actions.save")}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mt-2 text-[11px] text-gray-500">
          {t("consent.reset.prefix")}{" "}
          <button
            className="underline hover:text-gray-700"
            onClick={resetConsent}
            aria-label={t("consent.reset.button")}
          >
            {t("consent.reset.button")}
          </button>
          {t("consent.reset.suffix")}
        </div>
      </div>
    </div>
  );
}
